import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@pantry/ui';
import { useLeaveHousehold } from '@pantry/api-client';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';

interface LeaveHouseholdDialogProps {
  householdId: string;
  householdName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeaveHouseholdDialog({
  householdId,
  householdName,
  open,
  onOpenChange,
}: LeaveHouseholdDialogProps) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const leaveHousehold = useLeaveHousehold();

  const handleLeave = async () => {
    setError(null);

    try {
      await leaveHousehold.mutateAsync(householdId);
      onOpenChange(false);
      navigate({ to: '/households' });
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to leave household`;
      setError(message);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
              <ArrowRightStartOnRectangleIcon className="h-5 w-5 text-destructive" />
            </div>
            <Trans>Leave Household</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">
            <Trans>
              Are you sure you want to leave{' '}
              <span className="font-medium text-foreground">{householdName}</span>? You will lose
              access to all items and data in this household. You can only rejoin if someone invites
              you again.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
            {error}
          </div>
        )}
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel disabled={leaveHousehold.isPending} className="rounded-xl">
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleLeave}
            disabled={leaveHousehold.isPending}
            className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
          >
            {leaveHousehold.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <Trans>Leaving...</Trans>
              </div>
            ) : (
              <Trans>Leave Household</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
